import { useRef, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import CircularProgress from '@mui/material/CircularProgress'
import AnimatedHeading from '@/Components/UI/AnimatedHeading'

export type EventSearchMode = 'event' | 'venue'

interface Props {
  value: string
  onChange: (value: string) => void
  onSearch: () => void
  searchMode: EventSearchMode
  loading?: boolean
  locationValue?: string
  onLocationChange?: (label: string) => void
  onLocationSelect?: (placeId: string, label: string) => void
}

const EventSearchInput = ({
  value,
  onChange,
  onSearch,
  searchMode,
  loading = false,
  locationValue = '',
  onLocationChange,
  onLocationSelect,
}: Props) => {
  const [predictions, setPredictions] = useState<{ label: string; placeId: string }[]>([])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null)
  const locationInputRef = useRef<HTMLInputElement | null>(null)

  const getService = () => {
    if (!serviceRef.current && typeof google !== 'undefined') {
      serviceRef.current = new google.maps.places.AutocompleteService()
    }
    return serviceRef.current
  }

  const onLocationInput = (val: string) => {
    onLocationChange?.(val)
    const service = getService()
    if (!val.trim() || !service) {
      setPredictions([])
      setDropdownOpen(false)
      return
    }
    service.getPlacePredictions({ input: val, types: ['geocode'] }, (results) => {
      if (!results) {
        setDropdownOpen(false)
        return
      }
      setPredictions(results.map((r) => ({ label: r.description, placeId: r.place_id })))
      setDropdownOpen(true)
    })
  }

  const handleSearch = () => {
    if (dropdownOpen && predictions.length > 0) {
      onPredictionSelect(predictions[0].placeId, predictions[0].label)
    }
    onSearch()
  }

  const onPredictionSelect = (placeId: string, label: string) => {
    onLocationSelect?.(placeId, label)
    onLocationChange?.(label)
    setPredictions([])
    setDropdownOpen(false)
  }

  const searchButton = (extraClass = '') => (
    <button
      type='button'
      onClick={handleSearch}
      disabled={loading}
      className={`bg-[#af231c] hover:bg-[#8b1a15] transition-colors 
  rounded-full !p-2 w-12 h-12 flex items-center justify-center 
  shrink-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed 
  ${extraClass}`}
      aria-label='Search'>
      {loading ? (
        <CircularProgress size={16} sx={{ color: 'white' }} />
      ) : (
        <SearchIcon width={16} height={16} className='text-white' />
      )}
    </button>
  )

  return (
    <div className='flex flex-col gap-8'>
      <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
        {searchMode === 'venue' ? (
          <AnimatedHeading size='small'>Search by venue</AnimatedHeading>
        ) : (
          <AnimatedHeading size='small'>Search for a previous event:</AnimatedHeading>
        )}
      </div>

      <div className='relative'>
        <div
          className='border border-[#bfbfbf] rounded-2xl md:rounded-[100px] 
          p-2 flex flex-col md:flex-row md:items-center bg-white'>
          {searchMode === 'venue' && onLocationChange ? (
            <>
              {/* Venue name field */}
              <div className='flex-1 px-4 pt-2 pb-3 md:py-0 flex flex-col min-w-0'>
                <span className='font-semibold text-[14px] leading-[22px] tracking-[0.07px] text-black'>
                  Venue name
                </span>
                <input
                  type='text'
                  className='text-[16px] text-black leading-6 outline-none bg-transparent
                    placeholder:text-[#bfbfbf] font-normal w-full'
                  placeholder='e.g. Circular Quay restaurants'
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      locationInputRef.current?.focus()
                    }
                  }}
                />
              </div>

              {/* Mobile: divider line + button in one stack */}

              <div className='h-px bg-[#bfbfbf] mx-3 md:hidden' />

              {/* Desktop: vertical divider */}
              <div className='hidden md:block w-px h-8 bg-[#bfbfbf] shrink-0' />

              <div className='flex items-center flex-1 min-w-0'>
                <div className='px-4 pt-3 pb-2 md:py-0 flex flex-col min-w-0 md:flex-1'>
                  <span className='font-semibold text-[14px] leading-[22px] tracking-[0.07px] text-black'>
                    Location
                  </span>
                  <input
                    ref={locationInputRef}
                    type='text'
                    className='text-[16px] text-black leading-6 outline-none bg-transparent
                  placeholder:text-[#bfbfbf] font-normal w-full'
                    placeholder='e.g. London'
                    value={locationValue}
                    onChange={(e) => onLocationInput(e.target.value)}
                    onFocus={() => predictions.length > 0 && setDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        if (dropdownOpen && predictions.length > 0) {
                          onPredictionSelect(predictions[0].placeId, predictions[0].label)
                        }
                        onSearch()
                      }
                    }}
                  />
                </div>
                {searchButton('hidden md:flex')}
              </div>

              {/* Mobile only: full-width search button */}
              <div className='px-2 pb-2 pt-1 md:hidden'>
                <button
                  type='button'
                  onClick={handleSearch}
                  disabled={loading}
                  className='w-full bg-[#af231c] hover:bg-[#8b1a15] transition-colors
                  rounded-xl py-3 flex items-center justify-center gap-2
                  cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                  aria-label='Search'>
                  {loading ? (
                    <CircularProgress size={16} sx={{ color: 'white' }} />
                  ) : (
                    <>
                      <SearchIcon className='text-white' sx={{ fontSize: 20 }} />
                      <span className='text-white font-semibold text-[15px]'>Search</span>
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className='flex items-center flex-1 min-w-0'>
              <div className='flex-1 px-4 py-2 md:py-0 flex flex-col min-w-0'>
                <span className='font-semibold text-[14px] leading-[22px] tracking-[0.07px] text-black'>
                  {searchMode === 'event' ? 'Search by event name or date' : 'Venue name'}
                </span>
                <input
                  type='text'
                  className='text-[16px] text-black leading-6 outline-none bg-transparent
                  placeholder:text-[#bfbfbf] font-normal w-full'
                  placeholder={
                    searchMode === 'event'
                      ? 'e.g. Summer party 2026'
                      : 'e.g. Circular Quay restaurants'
                  }
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      onSearch()
                    }
                  }}
                />
              </div>
              {searchButton()}
            </div>
          )}
        </div>

        {dropdownOpen && predictions.length > 0 && (
          <ul
            className='absolute top-full right-0 w-full md:w-1/2 mt-1 bg-white
          border border-[#bfbfbf] rounded-2xl shadow-lg z-50 overflow-hidden'>
            {predictions.map((p) => (
              <li
                key={p.placeId}
                className='px-5 py-3 cursor-pointer hover:bg-[#f5f5f5] text-sm text-black'
                onMouseDown={() => onPredictionSelect(p.placeId, p.label)}>
                {p.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default EventSearchInput
